import type { CompanyLookupResponse } from '~/types/address'

export const MAX_CLIENT_EMAILS = 5

export interface CompanyView {
  name: string
  uic: string
  vat: string | null
  /** Only from the register lookup; the client's admAddress already contains the town. */
  city: string | null
  address: string | null
}

export type ClientEmailErrorCode = 'required' | 'too_many' | 'invalid' | 'duplicate'

export interface ClientEmailsCheck {
  rows: (ClientEmailErrorCode | '')[]
  list: ClientEmailErrorCode | ''
}

export interface ClientInvoiceApiErrors {
  eik: string
  nInvoiceTypeId: string
  phones: { rows: string[], list: string }
  emails: { rows: string[], list: string }
}

export function normalizeEik(raw: string | null | undefined): string {
  return (raw ?? '').replace(/\s+/g, '')
}

export function isValidEik(raw: string): boolean {
  return /^\d{9}$/.test(normalizeEik(raw))
}

export function companyFromLookup(lookup: CompanyLookupResponse): CompanyView {
  return {
    name: lookup.name,
    uic: lookup.uic,
    vat: lookup.vat,
    city: lookup.city,
    address: lookup.address,
  }
}

/** 1-5 emails, each with a valid shape, no duplicates (case-insensitive); same codes as the API. */
export function checkClientEmails(emails: string[]): ClientEmailsCheck {
  const rows: (ClientEmailErrorCode | '')[] = emails.map(() => '')
  let list: ClientEmailErrorCode | '' = ''
  if (!emails.length) list = 'required'
  else if (emails.length > MAX_CLIENT_EMAILS) list = 'too_many'
  const seen = new Set<string>()
  emails.forEach((raw, i) => {
    const email = raw.trim()
    if (!email) rows[i] = 'required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) rows[i] = 'invalid'
    else if (seen.has(email.toLowerCase())) rows[i] = 'duplicate'
    seen.add(email.toLowerCase())
  })
  return { rows, list }
}

/** HTTP status of an ofetch error, or 0. */
export function apiErrorStatus(error: unknown): number {
  const e = error as { status?: number, statusCode?: number, response?: { status?: number } } | null
  return e?.status ?? e?.statusCode ?? e?.response?.status ?? 0
}

export type LinkedClientStatus = 'loading' | 'linked' | 'none' | 'error'

/** A failed client-info load: only 404 means the account has no linked client. */
export function clientInfoStatus(error: unknown): Exclude<LinkedClientStatus, 'loading' | 'linked'> {
  return apiErrorStatus(error) === 404 ? 'none' : 'error'
}

/**
 * Error codes per field from a 422 / 429 / 503 of the client-invoice and company-lookup endpoints
 * (`detail.errors`: `{field: "eik" | "nInvoiceTypeId" | "phones[1]" | "emails", code}`).
 */
export function clientInvoiceApiErrors(error: unknown, phoneCount: number, emailCount: number): ClientInvoiceApiErrors {
  const result: ClientInvoiceApiErrors = {
    eik: '',
    nInvoiceTypeId: '',
    phones: { rows: Array.from({ length: phoneCount }, () => ''), list: '' },
    emails: { rows: Array.from({ length: emailCount }, () => ''), list: '' },
  }
  const detail = (error as { data?: { detail?: unknown } } | null)?.data?.detail
  const errors = (detail as { errors?: unknown } | null)?.errors
  if (!Array.isArray(errors)) return result
  for (const item of errors as { field?: string, code?: string }[]) {
    if (!item?.field || !item.code) continue
    if (item.field === 'eik') result.eik = item.code
    else if (item.field === 'nInvoiceTypeId') result.nInvoiceTypeId = item.code
    else {
      const match = /^(phones|emails)(?:\[(\d+)\])?$/.exec(item.field)
      if (!match) continue
      const target = result[match[1] as 'phones' | 'emails']
      const index = match[2] === undefined ? -1 : Number(match[2])
      if (index >= 0 && index < target.rows.length) target.rows[index] = item.code
      else target.list = item.code
    }
  }
  return result
}

export function hasClientInvoiceApiErrors(errors: ClientInvoiceApiErrors): boolean {
  return !!(errors.eik || errors.nInvoiceTypeId || errors.phones.list || errors.emails.list
    || errors.phones.rows.some(Boolean) || errors.emails.rows.some(Boolean))
}
